// Secrets out of analytics. Several emailed links carry a bearer token in the URL: password reset
// and invitations (`/reset-password?token=…`, a 2h / 7d account takeover), newsletter confirm /
// unsubscribe (`?token=…`) and outreach unsubscribe (`/outreach/u/:token`). Any URL we hand to
// PostHog (our `$pageview`, and the `$current_url` / `$pathname` / referrer it stamps on every
// autocaptured event) goes through here first. Pure TypeScript so it can be unit-tested.

// Query parameters whose name contains "token" (token, resetToken, access_token, …).
const SECRET_PARAM = /token/i

function decodedKey(part: string): string {
  const key = part.split('=')[0] ?? ''
  try {
    return decodeURIComponent(key.replace(/\+/g, ' '))
  } catch {
    return key
  }
}

/**
 * A path or full URL without its secrets: query parameters named like a token are dropped (the
 * others are kept exactly as written) and the token segment of `/outreach/u/:token` becomes
 * `:token`. Anything else comes back unchanged.
 */
export function sanitizeTrackedPath(value: string): string {
  const hashAt = value.indexOf('#')
  const hash = hashAt >= 0 ? value.slice(hashAt) : ''
  const beforeHash = hashAt >= 0 ? value.slice(0, hashAt) : value
  const queryAt = beforeHash.indexOf('?')
  const base = (queryAt >= 0 ? beforeHash.slice(0, queryAt) : beforeHash).replace(
    /\/outreach\/u\/[^/]+/,
    '/outreach/u/:token',
  )
  if (queryAt < 0) return base + hash
  const kept = beforeHash
    .slice(queryAt + 1)
    .split('&')
    .filter((part) => part !== '' && !SECRET_PARAM.test(decodedKey(part)))
  return base + (kept.length ? `?${kept.join('&')}` : '') + hash
}

// Where URLs sit in a PostHog event, at any depth of `properties`, `$set` and `$set_once`:
// - as VALUES under URL-like keys. posthog-js adds its own on every event — `$current_url`,
//   `$pathname`, `$referrer`, the session's `$session_entry_url` / `_pathname` / `_referrer` (the
//   full first URL of the session, query included), `$prev_pageview_pathname`, the person's
//   `$initial_current_url` / `_pathname` / `_referrer` — and its extensions nest more (web vitals:
//   `$web_vitals_<NAME>_event.$current_url`, attribution `url`). `path` is our own pageview property.
// - as KEYS: heatmaps send `$heatmap_data: { [location.href]: points[] }`.
// The walk covers both kinds anywhere, so a new PostHog structure is covered without a code change.
const URL_KEY = /(^\$?|_)(current_url|url|pathname|referrer|href)$/

function isUrlKey(key: string): boolean {
  return URL_KEY.test(key) || key === 'path'
}

// A key that is itself a URL or a path (heatmap maps).
function looksLikeUrl(key: string): boolean {
  return key.startsWith('/') || /^[a-z][a-z0-9+.-]*:\/\//i.test(key)
}

// How deep the walk goes below `properties` / `$set` / `$set_once`; deeper values are left as is.
const MAX_DEPTH = 6

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

// Sets an own property even for keys like `__proto__` (plain assignment would set the prototype).
function defineOwn(target: Record<string, unknown>, key: string, value: unknown) {
  Object.defineProperty(target, key, { value, enumerable: true, writable: true, configurable: true })
}

/**
 * Copy-on-write walk: returns `value` itself when nothing under it changed, else a cleaned copy.
 * Plain objects and arrays only (Dates, class instances and primitives in arrays are left alone).
 * `path` holds the objects on the current path only, so a cycle is skipped while an object shared
 * by two branches is cleaned in both; with the MAX_DEPTH cap the walk always ends. It could still
 * throw on a throwing getter or a revoked Proxy, but posthog-js deep-copies event data into plain
 * objects before `before_send` (all but its exception and replay captures), so none reaches it.
 */
function clean(value: unknown, depth: number, path: WeakSet<object>): unknown {
  if (value === null || typeof value !== 'object' || depth > MAX_DEPTH || path.has(value)) return value
  if (Array.isArray(value)) {
    path.add(value)
    let copy: unknown[] | null = null
    for (let i = 0; i < value.length; i++) {
      const item: unknown = value[i]
      if (item === null || typeof item !== 'object') continue
      const next = clean(item, depth + 1, path)
      if (next !== item) {
        copy ??= value.slice()
        copy[i] = next
      }
    }
    path.delete(value)
    return copy ?? value
  }
  if (!isPlainObject(value)) return value
  path.add(value)

  // Keys may collapse (two token URLs clean to one): collect in a Map, then build the object.
  const entries = new Map<string, unknown>()
  let changed = false
  for (const key of Object.keys(value)) {
    const item = value[key]
    const nextKey = looksLikeUrl(key) ? sanitizeTrackedPath(key) : key
    const nextItem =
      typeof item === 'string' ? (isUrlKey(key) ? sanitizeTrackedPath(item) : item) : clean(item, depth + 1, path)
    if (nextKey !== key || nextItem !== item) changed = true
    if (entries.has(nextKey)) {
      const prev = entries.get(nextKey)
      // Heatmap points: merge in order. Anything else: the first one stays.
      if (Array.isArray(prev) && Array.isArray(nextItem)) entries.set(nextKey, [...prev, ...nextItem])
    } else {
      entries.set(nextKey, nextItem)
    }
  }
  path.delete(value)
  if (!changed) return value
  const out: Record<string, unknown> = {}
  for (const [key, item] of entries) defineOwn(out, key, item)
  return out
}

interface CaptureLike {
  properties?: Record<string, unknown>
  $set?: Record<string, unknown>
  $set_once?: Record<string, unknown>
}

/**
 * PostHog `before_send`: every event (pageviews, autocapture, page leave, heatmaps, web vitals)
 * leaves with the URLs in `properties`, `$set` and `$set_once` sanitized — as values under URL-like
 * keys and as URL keys, at any depth up to MAX_DEPTH. Never mutates the event it is given and hands
 * back the same event when nothing needed cleaning. Not covered: autocapture's `$elements_chain`
 * string (attributes of the clicked element); no page renders a link with a token in it.
 */
export function sanitizeCapture<T extends CaptureLike | null>(event: T): T {
  if (!event) return event
  const path = new WeakSet<object>()
  const properties = clean(event.properties, 0, path) as Record<string, unknown> | undefined
  const $set = clean(event.$set, 0, path) as Record<string, unknown> | undefined
  const $set_once = clean(event.$set_once, 0, path) as Record<string, unknown> | undefined
  if (properties === event.properties && $set === event.$set && $set_once === event.$set_once) return event
  const out = { ...event, properties }
  if ($set !== undefined) out.$set = $set
  if ($set_once !== undefined) out.$set_once = $set_once
  return out
}
